import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, TableRow, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox.tsx';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { BankIncomeTotals } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/BankIncomeTotals.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import { FinancialConditionTableCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableCell.tsx';
import { FinancialConditionTableDeleteCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableDeleteCell.tsx';
import {
  BorrowerType,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { FINANCIAL_CONDITION_FIELD_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import {
  createBankIncomeRow,
  filterBankOptions,
  getBankIncomeTotals,
  normalizeDecimalValue,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';
import { useDocumentGenerationOptions } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useDocumentGenerationOptions.ts';
import {
  BANK_INCOME_SECTION_TITLE,
  BANK_INCOME_TABLE_ARIA_LABEL,
  BANK_INCOME_TABLE_COLUMN_WIDTHS,
  BANK_INCOME_TABLE_HEADERS,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/constants.ts';

interface IBankIncomeSectionProps {
  disabled: boolean;
}

export const BankIncomeSection: React.FC<IBankIncomeSectionProps> = ({ disabled }) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const isSoleTrader = borrowerType === BorrowerType.SoleTrader;
  const isLegalEntity = borrowerType === BorrowerType.LegalEntity;
  const hasCashFlowTransferRequirement = watch('hasCashFlowTransferRequirement');
  const incomeBanks = watch('incomeBanks');
  const { fields, append, remove } = useFieldArray({ control, name: 'incomeBanks' });
  const { bankOptions, currencyOptions, isBanksLoading, isCurrenciesLoading } =
    useDocumentGenerationOptions();
  const shouldShow = isSoleTrader || (isLegalEntity && hasCashFlowTransferRequirement);
  const totals = getBankIncomeTotals(incomeBanks);

  if (!borrowerType) {
    return null;
  }

  return (
    <>
      {isLegalEntity && (
        <FormCheckbox
          name="hasCashFlowTransferRequirement"
          label="Є вимога щодо переведення до Банку чистих грошових потоків"
          disabled={disabled}
          formControlLabelProps={{ sx: documentGenerationFormStyles.checkbox }}
        />
      )}

      {shouldShow && (
        <Box sx={documentGenerationFormStyles.section}>
          <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
            {BANK_INCOME_SECTION_TITLE}
          </Typography>
          <FinancialConditionTable
            ariaLabel={BANK_INCOME_TABLE_ARIA_LABEL}
            headers={[...BANK_INCOME_TABLE_HEADERS]}
            showActions={fields.length > 1}
            columnWidths={[...BANK_INCOME_TABLE_COLUMN_WIDTHS]}
          >
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <FinancialConditionTableCell>
                  <FormAutocomplete
                    name={`incomeBanks.${index}.bankId`}
                    label={FINANCIAL_CONDITION_FIELD_LABELS.bank}
                    options={bankOptions}
                    loading={isBanksLoading}
                    filterOptions={filterBankOptions}
                    shouldSaveLabel
                    disabled={disabled}
                    fullWidth
                    sx={documentGenerationFormStyles.field}
                  />
                </FinancialConditionTableCell>
                <FinancialConditionTableCell>
                  <FormAutocomplete
                    name={`incomeBanks.${index}.currency`}
                    label={FINANCIAL_CONDITION_FIELD_LABELS.currency}
                    options={currencyOptions}
                    preserveOptionsOrder
                    loading={isCurrenciesLoading}
                    disabled={disabled}
                    fullWidth
                    sx={documentGenerationFormStyles.field}
                  />
                </FinancialConditionTableCell>
                <FinancialConditionTableCell>
                  <FormInput
                    name={`incomeBanks.${index}.total`}
                    label="Всього"
                    type="text"
                    inputProps={{ inputMode: 'decimal' }}
                    normalizeOnBlur={normalizeDecimalValue}
                    disabled={disabled}
                    fullWidth
                    sx={documentGenerationFormStyles.field}
                  />
                </FinancialConditionTableCell>
                <FinancialConditionTableCell>
                  <FormInput
                    name={`incomeBanks.${index}.salesRevenue`}
                    label="Виручка від реалізації"
                    type="text"
                    inputProps={{ inputMode: 'decimal' }}
                    normalizeOnBlur={normalizeDecimalValue}
                    disabled={disabled}
                    fullWidth
                    sx={documentGenerationFormStyles.field}
                  />
                </FinancialConditionTableCell>
                <FinancialConditionTableDeleteCell
                  label={`Видалити банк ${index + 1}`}
                  disabled={disabled}
                  hidden={fields.length === 1}
                  onDelete={() => remove(index)}
                />
              </TableRow>
            ))}
          </FinancialConditionTable>

          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => append(createBankIncomeRow())}
            disabled={disabled}
            sx={documentGenerationFormStyles.addButton}
          >
            Додати банк
          </Button>

          <BankIncomeTotals totals={totals} />
        </Box>
      )}
    </>
  );
};
